<template>
  <v-container v-if="data_transactions && data_transactions.length" fluid class="pa-3 pa-md-6 bg-light-soft min-h-screen">
    <v-row justify="center">
      <v-col cols="12" xl="10">
        <!-- Encabezado -->
        <div class="d-flex flex-wrap align-center mb-4 header-gap">
          <v-btn v-if="hasHistory" icon @click="$router.go(-1)" class="bg-white shadow-sm">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <v-avatar :color="isCheck ? 'blue lighten-5' : 'teal lighten-5'" size="48" class="rounded-lg">
            <v-icon :color="isCheck ? 'blue darken-2' : 'teal darken-2'" size="28">
              {{ isCheck ? "mdi-bank-check" : "mdi-bank-transfer" }}
            </v-icon>
          </v-avatar>
          <div class="flex-grow-1 min-w-0">
            <div class="text-overline grey--text lh-tight">
              {{ tx.payment_type_name }} · {{ tx.business_unit_description }}
            </div>
            <h1 class="text-h5 text-md-h4 font-weight-black grey--text text--darken-4 mb-0">
              Transacción #{{ filLeft(tx.payment_transaction_id.toString(), 8, "0") }}
            </h1>
          </div>
          <v-chip :color="getColor(tx.status_id)" class="font-weight-bold" dark>
            <v-icon small left>{{ statusIcon }}</v-icon>
            {{ tx.status_name }}
          </v-chip>
        </div>

        <!-- Progreso de la transacción -->
        <v-card class="rounded-xl border-soft shadow-sm bg-white mb-4" elevation="0">
          <div class="steps">
            <div v-for="(step, i) in steps" :key="step.key" class="step" :class="`step--${step.state}`">
              <div class="step__dot">
                <v-icon small :color="step.state === 'todo' ? 'grey lighten-1' : 'white'">{{ step.icon }}</v-icon>
              </div>
              <div class="min-w-0">
                <div class="text-caption font-weight-black text-uppercase step__title">{{ step.title }}</div>
                <div class="text-caption grey--text text--darken-1 text-truncate">{{ step.detail }}</div>
              </div>
              <div v-if="i < steps.length - 1" class="step__line d-none d-md-block"></div>
            </div>
          </div>
        </v-card>

        <!-- Siguiente paso + acciones -->
        <v-card class="rounded-xl shadow-sm mb-6 action-card" :class="`action-card--${nextStep.tone}`" elevation="0">
          <div class="d-flex flex-wrap align-center pa-4 action-gap">
            <v-icon :color="nextStep.color" class="mr-1">{{ nextStep.icon }}</v-icon>
            <div class="flex-grow-1 min-w-0 mr-2">
              <div class="text-subtitle-2 font-weight-bold grey--text text--darken-3">{{ nextStep.title }}</div>
              <div class="text-caption grey--text text--darken-1">{{ nextStep.detail }}</div>
            </div>

            <!-- Pendiente -->
            <template v-if="isPending && can.approve">
              <v-btn color="error" outlined class="rounded-lg text-none" :loading="approving === 3"
                :disabled="!!approving" @click="AprroveOrReject(false)">
                <v-icon left size="18">mdi-thumb-down-outline</v-icon>Rechazar
              </v-btn>
              <v-btn color="success" depressed class="rounded-lg text-none" :loading="approving === 2"
                :disabled="!!approving" @click="AprroveOrReject(true)">
                <v-icon left size="18">mdi-thumb-up-outline</v-icon>Aprobar
              </v-btn>
            </template>

            <!-- Aprobada: cheques -->
            <template v-if="isApproved && isCheck && can.print">
              <v-btn color="primary" depressed class="rounded-lg text-none" :loading="loadingPrint"
                :disabled="checkIsPrint || selected.length === 0" @click="generate()">
                <v-icon left size="18">mdi-printer</v-icon>
                {{ checkIsPrint ? "Impreso" : `Imprimir ${selected.length || ""} cheque${selected.length === 1 ? "" : "s"}` }}
              </v-btn>
              <v-btn outlined color="primary" class="rounded-lg text-none" :loading="resumePrinting" @click="openResume">
                <v-icon left size="18">mdi-printer-search</v-icon>Reimprimir desde…
              </v-btn>
            </template>

            <!-- Aprobada: transferencias -->
            <v-btn v-if="isApproved && !isCheck && can.bankFile" color="primary" depressed class="rounded-lg text-none"
              @click="generate()">
              <v-icon left size="18">mdi-file-download-outline</v-icon>Generar archivo de banco
            </v-btn>

            <!-- Reportes y más opciones -->
            <v-menu v-if="isApproved || moreActions.length" offset-y left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn outlined class="rounded-lg text-none" v-bind="attrs" v-on="on">
                  <v-icon left size="18">mdi-dots-horizontal</v-icon>Más
                </v-btn>
              </template>
              <v-list dense class="rounded-lg py-1">
                <template v-if="isApproved">
                  <v-subheader class="text-caption">Reportes</v-subheader>
                  <v-list-item @click="generatePDF()">
                    <v-list-item-icon class="mr-3"><v-icon small color="red darken-1">mdi-file-pdf-box</v-icon></v-list-item-icon>
                    <v-list-item-title>Reporte PDF</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="generateExcel()">
                    <v-list-item-icon class="mr-3"><v-icon small color="green darken-1">mdi-microsoft-excel</v-icon></v-list-item-icon>
                    <v-list-item-title>Excel</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="isCheck" @click="generarListadoPendiente">
                    <v-list-item-icon class="mr-3">
                      <v-icon small :color="hasOverflowChecks ? 'warning' : 'grey'">mdi-file-document-alert-outline</v-icon>
                    </v-list-item-icon>
                    <v-list-item-title>{{ hasOverflowChecks ? "Generar listado pendiente" : "Listado pendiente" }}</v-list-item-title>
                  </v-list-item>
                </template>
                <template v-if="moreActions.length">
                  <v-divider v-if="isApproved" class="my-1"></v-divider>
                  <v-list-item v-for="action in moreActions" :key="action.key" @click="action.run()">
                    <v-list-item-icon class="mr-3"><v-icon small color="error">{{ action.icon }}</v-icon></v-list-item-icon>
                    <v-list-item-title class="error--text font-weight-bold">{{ action.label }}</v-list-item-title>
                  </v-list-item>
                </template>
              </v-list>
            </v-menu>
          </div>
        </v-card>

        <!-- Lotes enviados a la impresora sin confirmar (p. ej. se recargó la página) -->
        <v-alert v-for="job in unconfirmedJobs" :key="job.jobId" dense text type="warning" class="rounded-lg mb-4">
          <div class="d-flex flex-wrap align-center action-gap">
            <div class="flex-grow-1">
              {{ job.uncertain ? "No se confirmó si se imprimió" : "Falta confirmar" }} el lote del
              <b>{{ getDateTime(job.createdAt) }}</b> ({{ job.cheques.length }} cheque{{ job.cheques.length === 1 ? "" : "s" }}:
              {{ job.cheques.map(c => "#" + c.number).join(", ") }}). Revisa el papel e indica cómo salió.
            </div>
            <v-btn v-if="can.print" small color="warning" depressed class="text-none" @click="openPostPrint(job)">
              Confirmar resultado
            </v-btn>
          </div>
        </v-alert>

        <!-- Resumen -->
        <v-row dense class="mb-4">
          <v-col cols="12" sm="6" lg="3">
            <div class="info-tile">
              <div class="info-tile__label"><v-icon x-small color="primary" class="mr-1">mdi-cash-multiple</v-icon>Monto total</div>
              <div class="info-tile__value primary--text">
                {{ currencyFormat(tx.payment_transaction_amount) }}
                <span class="text-caption grey--text">{{ currency }}</span>
              </div>
              <div class="info-tile__hint">{{ beneficiaries.length }} beneficiario{{ beneficiaries.length === 1 ? "" : "s" }} · {{ invoicesCount }} factura{{ invoicesCount === 1 ? "" : "s" }}</div>
            </div>
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <div class="info-tile">
              <div class="info-tile__label"><v-icon x-small color="blue" class="mr-1">mdi-bank</v-icon>Cuenta bancaria</div>
              <div class="info-tile__value info-tile__value--sm text-truncate">{{ tx.bank_account_description_long }}</div>
              <div class="info-tile__hint text-truncate">{{ tx.bank_account_description_short }} · {{ tx.account_name }}</div>
            </div>
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <div class="info-tile">
              <div class="info-tile__label"><v-icon x-small color="purple" class="mr-1">mdi-calendar-clock</v-icon>Fechas</div>
              <div class="info-tile__row"><span>Pago</span><strong>{{ getDate(tx.payment_date) }}</strong></div>
              <div class="info-tile__row"><span>Contable</span><strong>{{ getDate(tx.accounting_date) }}</strong></div>
              <div class="info-tile__row"><span>Registro</span><strong>{{ getDate(tx.created_date) }}</strong></div>
            </div>
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <div class="info-tile">
              <div class="info-tile__label"><v-icon x-small color="grey darken-1" class="mr-1">mdi-account-check-outline</v-icon>Responsables</div>
              <div class="info-tile__row"><span>Elaborado</span><strong class="text-truncate ml-2">{{ tx.payment_by_name || "—" }}</strong></div>
              <div class="info-tile__row">
                <span>{{ statusActorLabel }}</span>
                <strong class="text-truncate ml-2">{{ tx.status_updated_by_name || (isPending ? "Pendiente" : "—") }}</strong>
              </div>
              <div v-if="tx.status_updated_at" class="info-tile__hint">el {{ getDateTime(tx.status_updated_at) }}</div>
            </div>
          </v-col>
        </v-row>

        <!-- Beneficiarios -->
        <v-card class="rounded-xl border-soft shadow-sm bg-white overflow-hidden mb-6" elevation="0">
          <div class="d-flex flex-wrap align-center px-4 py-3 action-gap">
            <v-icon color="primary">mdi-account-group-outline</v-icon>
            <span class="text-h6 font-weight-black grey--text text--darken-4">Beneficiarios</span>
            <v-chip color="primary lighten-5" class="primary--text font-weight-bold" label small>
              {{ beneficiaries.length }}
            </v-chip>
            <v-spacer></v-spacer>
            <template v-if="isCheck && isApproved && (can.print)">
              <span class="text-caption grey--text">{{ selected.length }} seleccionado{{ selected.length === 1 ? "" : "s" }}</span>
              <v-btn small text color="primary" class="text-none" @click="selected = [...beneficiaries]">Seleccionar todos</v-btn>
              <v-btn v-if="selected.length" small text class="text-none" @click="selected = []">Limpiar</v-btn>
            </template>
          </div>
          <v-divider></v-divider>

          <v-data-table v-model="selected" :headers="headers" :items="beneficiaries"
            :items-per-page="-1" class="custom-table" hide-default-footer :single-select="!isMultiple"
            :show-select="isCheck && isApproved && can.print" item-key="payment_transaction_sequence" :mobile-breakpoint="0">
            <template v-slot:[`item.check_sequence`]="{ item }">
              <v-chip small label outlined color="blue darken-2" class="font-weight-bold">#{{ item.check_sequence || "—" }}</v-chip>
            </template>
            <template v-slot:[`item.entity_name`]="{ item }">
              <div class="d-flex align-center py-2">
                <v-avatar size="32" color="primary lighten-5" class="mr-3">
                  <span class="text-subtitle-2 font-weight-black primary--text">{{ getInitials(item.entity_name) }}</span>
                </v-avatar>
                <div class="min-w-0">
                  <div class="text-body-2 font-weight-bold grey--text text--darken-4">{{ item.entity_name }}</div>
                  <div class="text-tiny grey--text font-weight-medium uppercase letter-spacing-1">
                    {{ item.document_id_type_name }}: {{ item.entity_document_id || "—" }}
                  </div>
                </div>
              </div>
            </template>
            <template v-slot:[`item.account`]="{ item }">
              <div v-if="item.account || item.bank_account_description_long">
                <div class="text-body-2 font-weight-medium">{{ item.bank_account_description_long }}</div>
                <div class="text-tiny grey--text">
                  <v-icon x-small color="grey lighten-1">mdi-credit-card-outline</v-icon>
                  {{ item.account }} - {{ item.account_type_name }}
                </div>
              </div>
              <span v-else class="text-caption" :class="isCheck ? 'grey--text' : 'error--text'">
                <v-icon v-if="!isCheck" x-small color="error">mdi-bank-off-outline</v-icon>
                {{ isCheck ? "—" : "Sin cuenta bancaria" }}
              </span>
            </template>
            <template v-slot:[`item.total_amount`]="{ item }">
              <div class="text-right text-no-wrap">
                <span class="text-body-2 font-weight-black grey--text text--darken-4">{{ currencyFormat(item.payment_amount) }}</span>
                <span class="text-tiny grey--text font-weight-bold ml-1">{{ item.currency_code }}</span>
              </div>
            </template>
            <template v-slot:[`item.invoices`]="{ item }">
              <v-chip x-small outlined>{{ (item.payment_transaction_invoice_details || []).length }}</v-chip>
            </template>
            <template v-slot:[`item.print_status`]="{ item }">
              <v-chip x-small label :color="printStateOf(item).color" text-color="white" class="font-weight-bold">
                {{ printStateOf(item).label }}
              </v-chip>
              <div v-if="printStateOf(item).reprints" class="text-tiny grey--text mt-1">
                Reimpreso ×{{ printStateOf(item).reprints }}
              </div>
            </template>
            <template v-slot:footer>
              <v-divider></v-divider>
              <div class="d-flex flex-wrap justify-end align-center px-6 py-4 bg-light-soft">
                <span class="text-tiny grey--text font-weight-bold uppercase letter-spacing-1 mr-3">Monto total</span>
                <span class="text-h5 font-weight-black primary--text">
                  {{ currencyFormat(tx.payment_transaction_amount) }}
                  <span class="text-body-2 ml-1">{{ currency }}</span>
                </span>
              </div>
            </template>
          </v-data-table>
        </v-card>

        <!-- Historial de impresión de cheques -->
        <v-card v-if="isCheck && printLog.length" class="rounded-xl border-soft shadow-sm bg-white mb-6" elevation="0">
          <div class="d-flex align-center px-6 py-4">
            <v-icon color="primary" class="mr-3">mdi-history</v-icon>
            <span class="text-subtitle-1 font-weight-black grey--text text--darken-4">Historial de impresión</span>
            <v-spacer></v-spacer>
            <v-btn small text class="text-none" @click="showAllHistory = !showAllHistory">
              {{ showAllHistory ? "Ver menos" : `Ver todo (${printLog.length})` }}
            </v-btn>
          </div>
          <v-divider></v-divider>
          <v-simple-table dense class="custom-table">
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Cheque</th>
                  <th>Tipo</th>
                  <th>Resultado</th>
                  <th>Usuario</th>
                  <th>Impresora</th>
                  <th>Motivo / detalle</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in (showAllHistory ? printLog : printLog.slice(0, 8))" :key="row.id"
                  :class="{ 'grey--text': !row.is_current }">
                  <td class="text-no-wrap">{{ getDateTime(row.created_at) }}</td>
                  <td class="text-no-wrap">
                    #{{ row.check_number || row.payment_transaction_sequence }}
                    <span v-if="!row.is_current" class="text-tiny">(anulado)</span>
                  </td>
                  <td>{{ row.kind === "REPRINT" ? "Reimpresión" : "Impresión" }}</td>
                  <td>
                    <v-chip x-small label :color="logStatus(row.status).color" text-color="white">{{ logStatus(row.status).label }}</v-chip>
                  </td>
                  <td class="text-no-wrap">
                    {{ row.created_by_name || "—" }}
                    <div v-if="row.updated_by_name && row.updated_by_name !== row.created_by_name" class="text-tiny grey--text">
                      Confirmó: {{ row.updated_by_name }}
                    </div>
                  </td>
                  <td class="text-no-wrap">{{ row.printer_name || "—" }}</td>
                  <td class="text-caption">{{ row.reason || row.error || "" }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </v-card>

        <!-- Desglose de facturas -->
        <div class="mb-3 d-flex align-center">
          <v-icon color="primary" class="mr-3">mdi-receipt-text-outline</v-icon>
          <span class="text-h6 font-weight-black grey--text text--darken-4">Desglose de facturas</span>
          <v-spacer></v-spacer>
          <v-btn small text class="text-none" @click="toggleAllPanels">
            {{ openPanels.length === beneficiaries.length ? "Contraer todo" : "Expandir todo" }}
          </v-btn>
        </div>

        <v-expansion-panels v-model="openPanels" multiple flat class="invoice-panels mb-8">
          <v-expansion-panel v-for="(beneficiary, i) in beneficiaries" :key="i" class="rounded-xl border-soft mb-3">
            <v-expansion-panel-header class="py-3">
              <div class="d-flex flex-wrap align-center action-gap">
                <v-avatar color="primary" size="30">
                  <span class="text-caption font-weight-black white--text">{{ getInitials(beneficiary.entity_name) }}</span>
                </v-avatar>
                <div class="min-w-0 flex-grow-1">
                  <div class="text-subtitle-2 font-weight-black grey--text text--darken-4">
                    <span v-if="isCheck && beneficiary.check_sequence" class="blue--text text--darken-2 mr-1">#{{ beneficiary.check_sequence }}</span>
                    {{ beneficiary.entity_name }}
                  </div>
                  <div class="text-tiny grey--text">
                    {{ beneficiary.payment_transaction_invoice_details.length }} factura(s)
                    <span v-if="beneficiary.account"> · {{ beneficiary.bank_account_description_long }} {{ beneficiary.account }}</span>
                  </div>
                </div>
                <span class="text-body-1 font-weight-black primary--text text-no-wrap mr-2">
                  {{ currencyFormat(beneficiary.payment_amount) }} {{ beneficiary.currency_code }}
                </span>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-simple-table class="custom-table" dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left"># Factura</th>
                      <th class="text-left">Ref. suplidor</th>
                      <th class="text-left">Emisión</th>
                      <th class="text-right">Monto total</th>
                      <th class="text-right">Monto pagado</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(invoice, n) in beneficiary.payment_transaction_invoice_details" :key="n"
                      @click="showInvoice(invoice.invoice_id, true)" class="cursor-pointer">
                      <td class="primary--text font-weight-black">#{{ invoice.invoice_number }}</td>
                      <td>
                        <v-chip x-small outlined label class="font-weight-bold" color="grey darken-1">{{ invoice.supplier_invoice_number || "N/A" }}</v-chip>
                      </td>
                      <td class="text-no-wrap">{{ getDate(invoice.emission_date) }}</td>
                      <td class="text-right grey--text text--darken-1 text-no-wrap">{{ currencyFormat(invoice.invoice_total_amount) }}</td>
                      <td class="text-right success--text font-weight-black text-no-wrap">{{ currencyFormat(invoice.payment_amount) }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>

    <!-- Cancel Transaction Dialog -->
    <v-dialog v-model="dialogCancelTransaction" max-width="500" persistent>
      <v-card class="rounded-xl overflow-hidden shadow-xl" elevation="0">
        <div class="pa-8 text-center bg-light-soft">
          <v-avatar color="error lighten-5" size="80" class="mb-6">
            <v-icon color="error" size="40">mdi-alert-circle-outline</v-icon>
          </v-avatar>
          <h2 class="text-h5 font-weight-black grey--text text--darken-4 mb-2">¿Anular transacción?</h2>
          <p class="text-body-2 grey--text font-weight-medium mb-0 px-4">
            Esta acción revertirá todos los pagos asociados a esta transacción bancaria. No se puede deshacer.
          </p>
        </div>

        <v-card-actions class="pa-6 border-top bg-white">
          <v-btn block depressed x-large
            class="rounded-xl text-capitalize font-weight-bold mb-3 grey--text text--darken-2" color="grey lighten-4"
            @click="dialogCancelTransaction = false">
            Mantener Transacción
          </v-btn>
          <v-btn block depressed x-large class="rounded-xl text-capitalize font-weight-bold error--text"
            color="error lighten-5" @click="cancelTransaction">
            Confirmar Anulación
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Anular y regenerar secuencias dañadas -->
    <v-dialog v-model="voidDialog" max-width="500" persistent>
      <v-card class="rounded-xl">
        <v-card-title class="text-h6">
          <v-icon left color="error">mdi-cog-off</v-icon> Anular y regenerar
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 grey--text text--darken-2 mb-2">
            Se anularán estas secuencias (cheques dañados) y cada pago se relinkeará al
            <b>siguiente número de cheque disponible</b>:
          </p>
          <v-list dense>
            <v-list-item v-for="(it, i) in voidList" :key="i" class="px-0">
              <v-list-item-icon class="mr-2">
                <v-icon small color="error">mdi-close-circle</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Cheque #{{ it.check_sequence }} — {{ it.entity_name }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <p class="text-caption error--text mb-0">
            Esta acción no se puede deshacer. Asegúrate de tener números de cheque disponibles.
          </p>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn text @click="voidDialog = false">Cancelar</v-btn>
          <v-btn color="error" depressed :loading="voidProcessing" @click="confirmVoidRegenerate">
            <v-icon left size="18">mdi-cog-off</v-icon> Anular y regenerar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmación antes de imprimir cheques -->
    <v-dialog v-model="printConfirmDialog" max-width="440" persistent>
      <v-card class="rounded-xl">
        <v-card-title class="text-h6">
          <v-icon left color="primary">mdi-printer</v-icon> Confirmar impresión
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 grey--text text--darken-2 mb-3">
            Se imprimirán <b>{{ printConfirmCount }}</b> cheque(s) en formulario continuo.
          </p>
          <printer-picker v-if="printConfirmDialog" v-model="printerName" @status="printService = $event"></printer-picker>
          <p class="text-caption error--text mb-0">
            Verifica que el papel esté alineado al inicio del cheque antes de continuar.
          </p>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn text @click="printConfirmDialog = false">Cancelar</v-btn>
          <v-btn color="primary" depressed :loading="loadingPrint"
            :disabled="!printService.online || !printService.printerOk" @click="confirmPrintChecks">
            <v-icon left size="18">mdi-printer</v-icon> Imprimir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Reimprimir desde un cheque dañado en adelante -->
    <v-dialog v-model="resumeDialog" max-width="520" persistent>
      <v-card class="rounded-xl">
        <v-card-title class="text-h6">
          <v-icon left color="primary">mdi-printer-search</v-icon> Reimprimir desde un cheque
        </v-card-title>
        <v-card-text>
          <p class="text-body-2 grey--text text--darken-1 mb-4">
            Si un cheque se dañó, elige desde cuál reanudar. Se reimprimirá <b>ese cheque y TODOS
            los siguientes</b>, en el mismo orden del lote.
          </p>
          <v-select v-model="resumeIndex" :items="resumeOptions" dense outlined
            label="Reimprimir desde…" class="mb-1"></v-select>
          <v-textarea v-model="resumeReason" dense outlined rows="2" auto-grow counter="500" maxlength="500"
            label="Motivo de la reimpresión (obligatorio)" hint="Ej.: papel atascado en el cheque #4012"
            persistent-hint class="mb-3"></v-textarea>
          <printer-picker v-if="resumeDialog" v-model="printerName" @status="printService = $event"></printer-picker>
          <p class="text-caption grey--text mb-0">
            Reimprimir usa el <b>mismo número de cheque</b>. Si el formulario físico se dañó, lo recomendado
            es «Anular y regenerar» para que el cheque salga con un número nuevo.
          </p>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer></v-spacer>
          <v-btn text @click="resumeDialog = false">Cancelar</v-btn>
          <v-btn color="primary" depressed :loading="resumePrinting"
            :disabled="resumeReason.trim().length < 5 || !printService.online || !printService.printerOk" @click="reprintFrom">
            <v-icon left size="18">mdi-printer</v-icon> Reimprimir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Después de imprimir: ¿salieron bien los cheques? -->
    <v-dialog v-model="postPrintDialog" max-width="560" persistent>
      <v-card v-if="postPrintJob" class="rounded-xl">
        <v-card-title class="text-h6">
          <v-icon left :color="postPrintJob.uncertain ? 'warning' : 'success'">
            {{ postPrintJob.uncertain ? "mdi-printer-alert" : "mdi-printer-check" }}
          </v-icon>
          {{ postPrintStep === "damaged" ? "¿Qué hacemos con los dañados?" : "¿Salieron bien los cheques?" }}
        </v-card-title>
        <v-card-text v-if="postPrintStep === 'check'">
          <v-alert v-if="postPrintJob.uncertain" dense text type="warning" class="text-caption">
            No se pudo confirmar con el servicio si el lote llegó a la impresora. Revisa el papel:
            si no salió nada, márcalos todos como dañados para reimprimir con motivo.
          </v-alert>
          <p class="text-body-2 grey--text text--darken-2 mb-2">
            Revisa el papel y marca los cheques que salieron <b>dañados o mal alineados</b>.
          </p>
          <v-list dense class="py-0">
            <v-list-item v-for="c in postPrintJob.cheques" :key="c.seq" class="px-0" @click="toggleDamaged(c.seq)">
              <v-list-item-action class="mr-3 my-0">
                <v-checkbox :input-value="damagedSeqs.includes(c.seq)" color="error" hide-details readonly></v-checkbox>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>#{{ c.number }} — {{ c.name }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-card-text v-else>
          <p class="text-body-2 grey--text text--darken-2">
            Quedaron registrados como dañados:
            <b>{{ postPrintJob.cheques.filter(c => damagedSeqs.includes(c.seq)).map(c => "#" + c.number).join(", ") }}</b>.
          </p>
          <p class="text-body-2 grey--text text--darken-2 mb-0">
            <b>Anular y regenerar</b> (recomendado) les asigna un número de cheque nuevo.
            <b>Reimprimir</b> usa el mismo número en un formulario nuevo.
          </p>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <template v-if="postPrintStep === 'check'">
            <v-btn text @click="postPrintDialog = false">Confirmar después</v-btn>
            <v-spacer></v-spacer>
            <v-btn v-if="damagedSeqs.length" color="error" depressed :loading="confirmingPrint" @click="confirmPostPrint">
              Registrar {{ damagedSeqs.length }} dañado{{ damagedSeqs.length === 1 ? "" : "s" }}
            </v-btn>
            <v-btn v-else color="success" depressed :loading="confirmingPrint" @click="confirmPostPrint">
              <v-icon left size="18">mdi-check</v-icon> Todos salieron bien
            </v-btn>
          </template>
          <template v-else>
            <v-btn text @click="postPrintDialog = false">Cerrar</v-btn>
            <v-spacer></v-spacer>
            <v-btn outlined color="primary" class="text-none" @click="reprintDamaged">Reimprimir</v-btn>
            <v-btn color="error" depressed class="text-none" @click="voidDamaged">
              <v-icon left size="18">mdi-cog-off</v-icon> Anular y regenerar
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Cheque interno PLAN EPAY (banco 22): vista previa + impresión A4 -->
    <internal-check-preview v-model="internalCheckDialog" :checks="internalChecks" />
  </v-container>
</template>

<script>
import moment from "moment";
import global_reports from "@/services/global_reports.js";
import check_print from "@/services/check_print.js";
import InternalCheckPreview from "@/views/CxP/InternalCheckPreview.vue";
import PrinterPicker from "@/components/CxP/PrinterPicker.vue";

// Estado de impresión por cheque, a partir del historial (FINANCES.CHECK_PRINT_LOG).
const PRINT_STATES = {
  none: { label: "Sin imprimir", color: "grey" },
  pending: { label: "Sin confirmar", color: "orange darken-1" },
  uncertain: { label: "Incierto", color: "deep-orange darken-2" },
  ok: { label: "Impreso", color: "success" },
  damaged: { label: "Dañado", color: "error" },
};
const LOG_STATUS = {
  SENDING: { label: "Enviando", color: "grey" },
  SENT: { label: "En cola", color: "orange darken-1" },
  UNCERTAIN: { label: "Incierto", color: "deep-orange darken-2" },
  FAILED: { label: "No se imprimió", color: "grey darken-1" },
  OK: { label: "Salió bien", color: "success" },
  DAMAGED: { label: "Dañado", color: "error" },
};

export default {
  components: { InternalCheckPreview, PrinterPicker },
  data: () => ({
    dialogCancelTransaction: false,
    data_transactions: null,
    loadingPrint: false,
    // Historial de impresión (persistido en BD): define qué cheques ya se imprimieron.
    printLog: [],
    showAllHistory: false,
    printService: { online: false, printerOk: false },
    postPrintDialog: false,
    postPrintStep: "check", // check | damaged
    postPrintJob: null, // { jobId, uncertain, cheques: [{ seq, number, name }] }
    damagedSeqs: [],
    confirmingPrint: false,
    resumeReason: "",
    isMultiple: true,
    selected: [],
    loading: false,
    id_transactions: null,
    hasHistory: false,
    // Reimpresión: último lote impreso (para reanudar desde un cheque dañado en adelante).
    lastBatch: null,
    resumeDialog: false,
    resumeIndex: null,
    resumePrinting: false,
    // Impresora de cheques (un solo lugar) y confirmación antes de imprimir.
    printerName: "PRINTERCHEQUES",
    printConfirmDialog: false,
    printConfirmCount: 0,
    // Anular + regenerar secuencias (cheques dañados -> siguiente número disponible).
    voidDialog: false,
    voidProcessing: false,
    // Cheque interno PLAN EPAY (banco 22): vista previa/impresión propia en A4.
    internalCheckDialog: false,
    internalChecks: [],
    approving: null,
    openPanels: [],
  }),
  computed: {
    // Permisos por acción: CXP-013 aprobar, CXP-014 cheques, CXP-015 anular, CXP-016 archivo de banco
    can() {
      return {
        approve: this.hasFeature("CXP-013"),
        print: this.hasFeature("CXP-014"),
        cancel: this.hasFeature("CXP-015"),
        bankFile: this.hasFeature("CXP-016"),
      };
    },
    tx() {
      return this.data_transactions[0];
    },
    isCheck() {
      return this.tx.invoice_payment_type_id === 3;
    },
    isPending() {
      return this.tx.status_id === 1;
    },
    isApproved() {
      return this.tx.status_id === 2;
    },
    beneficiaries() {
      return this.tx.payment_transaction_details || [];
    },
    // Estado por secuencia según el historial del número de cheque vigente.
    printStates() {
      const out = {};
      // printLog viene del más reciente al más antiguo.
      this.printLog.forEach((row) => {
        if (!row.is_current || row.status === "FAILED") return;
        const seq = row.payment_transaction_sequence;
        if (!out[seq]) {
          const state = { SENDING: "pending", SENT: "pending", UNCERTAIN: "uncertain", OK: "ok", DAMAGED: "damaged" }[row.status];
          out[seq] = { state, reprints: 0 };
        }
        if (row.kind === "REPRINT") out[seq].reprints++;
      });
      return out;
    },
    // Todos los cheques de la transacción tienen al menos una impresión registrada.
    checkIsPrint() {
      return this.beneficiaries.length > 0 &&
        this.beneficiaries.every((b) => this.printStates[b.payment_transaction_sequence]);
    },
    // Lotes enviados que nadie confirmó todavía (sobreviven a recargar la página).
    unconfirmedJobs() {
      const jobs = new Map();
      this.printLog.forEach((row) => {
        if (!row.is_current || !["SENT", "UNCERTAIN"].includes(row.status)) return;
        if (!jobs.has(row.job_id)) jobs.set(row.job_id, { jobId: row.job_id, createdAt: row.created_at, uncertain: false, cheques: [] });
        const job = jobs.get(row.job_id);
        if (row.status === "UNCERTAIN") job.uncertain = true;
        const b = this.beneficiaries.find((x) => x.payment_transaction_sequence === row.payment_transaction_sequence);
        job.cheques.push({
          seq: row.payment_transaction_sequence,
          number: row.check_number || row.payment_transaction_sequence,
          name: b ? b.entity_name : "",
        });
      });
      return [...jobs.values()].map((job) => ({ ...job, cheques: job.cheques.sort((a, b) => a.seq - b.seq) }));
    },
    currency() {
      return this.beneficiaries[0]?.currency_code || "";
    },
    invoicesCount() {
      return this.beneficiaries.reduce((sum, b) => sum + (b.payment_transaction_invoice_details || []).length, 0);
    },
    statusIcon() {
      return { 1: "mdi-clock-outline", 2: "mdi-check-circle", 3: "mdi-close-circle", 4: "mdi-cancel" }[this.tx.status_id] || "mdi-circle";
    },
    statusActorLabel() {
      return { 2: "Aprobado", 3: "Rechazado", 4: "Anulado" }[this.tx.status_id] || "Aprobación";
    },
    // Línea de tiempo: registrada -> aprobación -> impresión / archivo
    steps() {
      const t = this.tx;
      const who = t.status_updated_by_name ? `${t.status_updated_by_name}${t.status_updated_at ? " · " + this.getDate(t.status_updated_at) : ""}` : "";
      const decision = {
        1: { title: "Aprobación", detail: "Pendiente de aprobación", state: "current", icon: "mdi-clock-outline" },
        2: { title: "Aprobada", detail: who || "Aprobada", state: "done", icon: "mdi-check" },
        3: { title: "Rechazada", detail: who || "Rechazada", state: "error", icon: "mdi-close" },
        4: { title: "Anulada", detail: who || "Anulada", state: "error", icon: "mdi-cancel" },
      }[t.status_id] || { title: "Aprobación", detail: t.status_name, state: "todo", icon: "mdi-help" };
      const last = this.isCheck
        ? { title: "Impresión de cheques", icon: "mdi-printer" }
        : { title: "Archivo de banco", icon: "mdi-file-download-outline" };
      let lastState = "todo";
      let lastDetail = "Después de aprobar";
      if (this.isApproved) {
        lastState = this.checkIsPrint ? "done" : "current";
        lastDetail = this.checkIsPrint ? "Enviados a impresión" : this.isCheck ? "Listo para imprimir" : "Listo para generar";
      } else if (t.status_id === 3 || t.status_id === 4) {
        lastDetail = "No aplica";
      }
      return [
        { key: "created", title: "Registrada", detail: `${t.payment_by_name || ""} · ${this.getDate(t.created_date)}`, state: "done", icon: "mdi-file-document-edit-outline" },
        { key: "decision", ...decision },
        { key: "output", ...last, detail: lastDetail, state: lastState },
      ];
    },
    // Qué le toca hacer al usuario con esta transacción
    nextStep() {
      if (this.isPending) {
        return this.can.approve
          ? { tone: "warn", color: "orange darken-2", icon: "mdi-account-clock-outline", title: "Pendiente de aprobación", detail: "Revisa los beneficiarios y facturas, luego aprueba o rechaza." }
          : { tone: "warn", color: "orange darken-2", icon: "mdi-account-clock-outline", title: "Esperando aprobación", detail: "La debe aprobar un usuario con permiso de aprobar transacciones (CXP-013)." };
      }
      if (this.isApproved) {
        if (this.isCheck) {
          if (!this.can.print) return { tone: "ok", color: "success", icon: "mdi-check-circle-outline", title: "Aprobada", detail: "La impresión de cheques la hace un usuario con permiso CXP-014." };
          return this.checkIsPrint
            ? { tone: "ok", color: "success", icon: "mdi-printer-check", title: "Cheques enviados a impresión", detail: "Si un cheque se dañó, usa «Reimprimir desde…» o anula y regenera su secuencia en «Más»." }
            : { tone: "info", color: "primary", icon: "mdi-printer", title: "Lista para imprimir", detail: "Marca los cheques en la tabla de beneficiarios y pulsa Imprimir." };
        }
        return this.can.bankFile
          ? { tone: "info", color: "primary", icon: "mdi-file-download-outline", title: "Lista para el banco", detail: "Genera el archivo y cárgalo en la banca en línea." }
          : { tone: "ok", color: "success", icon: "mdi-check-circle-outline", title: "Aprobada", detail: "El archivo de banco lo genera un usuario con permiso CXP-016." };
      }
      if (this.tx.status_id === 3) return { tone: "error", color: "error", icon: "mdi-close-circle-outline", title: "Transacción rechazada", detail: "No se puede imprimir ni generar archivo." };
      return { tone: "error", color: "grey darken-1", icon: "mdi-cancel", title: "Transacción anulada", detail: "Los pagos volvieron a quedar pendientes para una nueva transacción." };
    },
    moreActions() {
      const actions = [];
      if (this.isApproved && this.isCheck && this.can.print && this.selected.length >= 1) {
        actions.push({ key: "void", icon: "mdi-cog-off", label: `Anular y regenerar secuencia${this.selected.length > 1 ? `s (${this.selected.length})` : ""}`, run: this.openVoidDialog });
      }
      if (this.isPending && this.can.cancel) {
        actions.push({ key: "cancel", icon: "mdi-delete-outline", label: "Anular transacción", run: () => { this.dialogCancelTransaction = true; } });
      }
      return actions;
    },
    // Opciones del diálogo "Reimprimir desde…": cada cheque del último lote, en orden impreso.
    resumeOptions() {
      if (!this.lastBatch || !this.lastBatch.cheques) return [];
      return this.lastBatch.cheques.map((c, i) => ({
        text: `#${c.CheckNumber || c.chequeNumber} — ${c.SupplierName}`,
        value: i,
      }));
    },
    // Seleccionados a anular, ordenados ascendente (para mapeo determinista 23->25, 24->26).
    voidList() {
      return [...this.selected].sort(
        (a, b) => (a.payment_transaction_sequence || 0) - (b.payment_transaction_sequence || 0)
      );
    },
    // ¿Algún cheque de la transacción paga más facturas de las que caben en el voucher?
    hasOverflowChecks() {
      const t = this.data_transactions && this.data_transactions[0];
      if (!t || t.invoice_payment_type_id !== 3 || !t.payment_transaction_details) return false;
      const max = check_print.MAX_INVOICES_PER_CHECK;
      return t.payment_transaction_details.some(
        (d) => (d.payment_transaction_invoice_details || []).length > max
      );
    },
    headers() {
      if (!this.data_transactions || !this.data_transactions[0]) {
        return [];
      }

      const baseHeaders = [];

      if (this.data_transactions[0].invoice_payment_type_id === 3) {
        baseHeaders.push({
          text: "Impresión",
          value: "print_status",
          sortable: false,
        });
        baseHeaders.push({
          text: "Número Cheque",
          value: "check_sequence",
          align: "start",
          sortable: false,
        });
      }

      baseHeaders.push(
        {
          text: "Seq",
          value: "payment_transaction_sequence",
          align: "start",
          sortable: false,
        },
        {
          text: "Beneficiario",
          value: "entity_name",
          sortable: false,
        },
        {
          text: "Cuenta Bancaria",
          value: "account",
          sortable: false,
        },
        {
          text: "Facturas",
          value: "invoices",
          align: "center",
          sortable: false,
        },
        {
          text: "Monto",
          value: "total_amount",
          align: "end",
          sortable: false,
        }
      );

      return baseHeaders;
    },
  },
  watch: {
    // Al navegar de una transacción a otra se reutiliza el componente: recargar
    "$route.params.id"(id, oldId) {
      if (id && id !== oldId) {
        this.selected = [];
        this.printLog = [];
        this.lastBatch = null;
        this.getData();
      }
    },
  },
  created() {
    this.getData();
  },
  mounted() {
    this.hasHistory = window.history.length > 2;
  },
  methods: {
    async getData() {
      this.loading = true;
      this.id_transactions = this.$route.params.id;

      const responseRequest = await this.api_Post(
        "transactions/viewAllTransactions",
        {
          id: this.id_transactions,
        }
      );
      if (responseRequest.status == 200) {
        this.data_transactions = await responseRequest.json();
        if (this.data_transactions?.[0]?.invoice_payment_type_id === 3) this.loadPrintLog();
        const count = this.data_transactions?.[0]?.payment_transaction_details?.length || 0;
        // Con pocos beneficiarios el desglose abre expandido
        this.openPanels = count <= 3 ? [...Array(count).keys()] : [];
      } else {
        this.$toast.error("error al cargar la transacción!");
      }
      this.loading = false;
    },
    openVoidDialog() {
      if (this.selected.length === 0) {
        this.$toast.warning("Selecciona la(s) secuencia(s) dañada(s).");
        return;
      }
      this.voidDialog = true;
    },
    // Anula cada secuencia seleccionada y relinkea su pago al siguiente número disponible.
    // Secuencial y en orden ascendente -> mapeo determinista (23->25, 24->26).
    async confirmVoidRegenerate() {
      this.voidProcessing = true;
      let ok = 0;
      let fail = 0;
      for (const item of this.voidList) {
        try {
          const result = await this.api_Post("transactions/updateCheckSequence", {
            in_payment_transaction_id: item.payment_transaction_id,
            in_payment_transaction_sequence: item.payment_transaction_sequence,
            user_id: this.$store.state.auth.user.user.user_id,
          });
          if (result.status == 200) ok++;
          else {
            fail++;
            if (result.status === 401 || result.status === 403) {
              const error = await result.json().catch(() => null);
              this.$toast.error(error?.message || "No tienes permiso para esta acción");
              break;
            }
          }
        } catch (e) {
          console.error(e);
          fail++;
        }
      }
      this.voidProcessing = false;
      this.voidDialog = false;
      this.selected = [];
      await this.getData();
      if (fail === 0) {
        this.$toast.success(`${ok} secuencia(s) anulada(s) y regenerada(s)`);
      } else {
        this.$toast.error(`${ok} regenerada(s), ${fail} con error (¿hay números disponibles?)`);
      }
    },
    async AprroveOrReject(type) {
      if (this.approving) return;
      let status = type === true ? 2 : 3;
      this.approving = status;
      let data = {
        transaction_id: this.id_transactions,
        status_id: status,
        user_id: this.$store.state.auth.user.user.user_id,
      };

      const responseApprove = await this.api_Post(
        "transactions/updateStatusTransaction",
        data
      );
      if (responseApprove.status == 200) {
        this.getData();
        this.$toast.success(type ? "Aprobado con éxito" : "Rechazado");
      } else {
        const error = await responseApprove.json().catch(() => null);
        this.$toast.error(error?.message || "Error al procesar la solicitud");
        if (responseApprove.status === 409) this.getData();
      }
      this.approving = null;
    },
    getDateTime(date) {
      return date ? moment(date).format("DD/MM/YYYY hh:mm A") : "";
    },
    toggleAllPanels() {
      this.openPanels = this.openPanels.length === this.beneficiaries.length ? [] : [...Array(this.beneficiaries.length).keys()];
    },
    async cancelTransaction() {
      this.loading = true;
      const responseRequest = await this.api_Post(
        "transactions/cancelTransaction",
        {
          id: this.id_transactions,
          user_id: this.$store.state.auth.user.user.user_id,
        }
      );
      if (responseRequest.status == 200) {
        this.getData();
        this.dialogCancelTransaction = false;
        this.$toast.success("Transacción anulada!");
      } else {
        const error = await responseRequest.json().catch(() => null);
        this.$toast.error(error?.message || "Error al anular transacción");
      }
      this.loading = false;
    },
    getColor(status_id) {
      const colors = {
        1: "orange",
        2: "green",
        3: "red",
        4: "grey darken-1",
      };
      return colors[status_id] || "grey";
    },
    getDate(date) {
      if (!date) return "N/A";
      return moment(date).format("DD/MM/YYYY");
    },
    getInitials(name) {
      if (!name) return "??";
      return name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
    },
    filLeft(str, len, char) {
      if (!str) return "0".repeat(len);
      return str.padStart(len, char);
    },
    async generate() {
      const transaction = this.data_transactions[0];
      if (transaction.status_id !== 2) {
        this.$toast.warning("La transacción debe estar aprobada.");
        return;
      }
      if (transaction.invoice_payment_type_id == 3 && !this.can.print) {
        this.$toast.error("No tienes permiso para imprimir cheques (CXP-014).");
        return;
      }
      if (transaction.invoice_payment_type_id == 4 && !this.can.bankFile) {
        this.$toast.error("No tienes permiso para generar el archivo de banco (CXP-016).");
        return;
      }
      if (transaction.invoice_payment_type_id == 3) {
        if (this.selected.length === 0) {
          this.$toast.warning("Marca en la tabla los cheques a imprimir.");
          return;
        }

        // Banco 22 = cheque INTERNO PLAN EPAY: vista previa/impresión propia
        // en A4 (HTML). Cualquier otro banco sigue el flujo de siempre
        // (formulario continuo vía el API de Windows).
        if (transaction.bank_id == 22 || transaction.bank_id == 48) {
          this.openInternalChecks();
          return;
        }

        const printed = this.selected.filter((b) => this.printStates[b.payment_transaction_sequence]);
        if (printed.length) {
          this.$toast.warning(
            `Ya impreso(s): ${printed.map((b) => "#" + (b.check_sequence || b.payment_transaction_sequence)).join(", ")}. ` +
            "Para volver a imprimirlos usa «Reimprimir desde…» con un motivo."
          );
          return;
        }
        try {
          const { printerName } = await this.resolveAccountCfg(transaction);
          this.printerName = printerName;
        } catch (error) {
          this.$toast.error(error.message || "No se pudo cargar la configuración de la cuenta.");
          return;
        }
        this.printService = { online: false, printerOk: false };
        this.printConfirmCount = this.selected.length;
        this.printConfirmDialog = true;
      } else if (transaction.invoice_payment_type_id == 4) {
        this.generateFile();
      }
    },
    // Abre la vista previa del cheque interno con los pagos seleccionados.
    openInternalChecks() {
      this.internalChecks = this.buildInternalChecksFrom(this.selected);
      this.internalCheckDialog = true;
    },
    // Prefijo del No. de cheque a partir de la unidad emisora (ej. "CODEVI, S.A." -> "COD").
    entityPrefix(desc) {
      const clean = String(desc || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
      return clean.slice(0, 3) || "PE";
    },
    // Arma los cheques internos SOLO con datos que ya trae la impresión de cheques
    // (vista finances.payment_transaction_with_details). No depende de prestaciones:
    // sirve para cualquier pago con cheque del banco interno.
    // Nota: KONSÈP usa invoices.note, que se agregó a la vista (ver backend/sql/payment.sql).
    // DEPT/ZÒN queda oculto por ahora (sin columna en la vista).
    buildInternalChecksFrom(list) {
      const t = this.data_transactions[0];
      const date = moment(t.payment_date).format("DD/MM/YYYY");
      const prefix = this.entityPrefix(t.business_unit_description);
      const uniqJoin = (arr) => [...new Set(arr.filter(Boolean))].join(", ");
      return (list || []).map((el) => {
        const invoices = el.payment_transaction_invoice_details || [];
        return {
          checkNumber: `${prefix}-${this.filLeft(
            String(el.check_sequence || el.payment_transaction_sequence || ""),
            6,
            "0"
          )}`,
          date,
          issuerShort: t.business_unit_description || "",
          issuerLong: "",
          payee: el.entity_name || "",
          currency: el.currency_code || "",
          amount: Number(el.payment_amount) || 0,
          identity: el.entity_document_id || "", // cédula/RNC del beneficiario
          employeeCode: uniqJoin(invoices.map((iv) => iv.supplier_invoice_number)), // KÒD
          concept: uniqJoin(invoices.map((iv) => iv.note)),                          // KONSÈP = nota de factura
          issuedBy: t.payment_by_name || "",     // usuario que registró el pago
          internalOrder: uniqJoin(invoices.map((iv) => iv.invoice_number)), // No. de factura(s)
          // Sin fuente en la vista de cheques -> se ocultan:
          department: "",
          requestNumber: "",
        };
      });
    },
    // Arma los ChequeDetails (para el renderizador) a partir de una lista de pagos.
    buildChequeDetailsFrom(list) {
      const transaction = this.data_transactions[0];
      const fechaFormateada = moment(transaction.payment_date).format("DD/MM/YYYY");
      return (list || []).map((element) => {
        let address = "";
        if (element.entity_contact_json) {
          element.entity_contact_json.forEach((contact) => {
            if (contact.id_type == 10) address = contact.description;
          });
        }
        const invoices = (element.payment_transaction_invoice_details || []).map((inv) => ({
          VoucherNo: inv.invoice_number,
          invoiceNo: inv.supplier_invoice_number || " ",
          invoiceDate: moment(inv.emission_date).format("DD/MM/YYYY"),
          invoiceAmount: inv.invoice_total_amount,
          amountPaid: inv.payment_amount,
          discountTaken: inv.discount_amount || 0,
          netCheckAmount: inv.payment_amount - (inv.discount_amount || 0),
        }));
        return {
          chequeDate: fechaFormateada,
          chequeNumber: element.payment_transaction_sequence.toString(),
          beneficiaryName: element.entity_name,
          amount: element.payment_amount.toString(),
          SupplierId: element.entity_id.toString(),
          SupplierName: element.entity_name,
          SupplierAddress: address,
          InvoiceDetails: invoices,
          // Para el registro de impresión (el API ignora estos campos).
          Sequence: element.payment_transaction_sequence,
          CheckNumber: element.check_sequence || null,
        };
      });
    },
    // Leer la configuración actual de la cuenta, incluidos los ajustes exclusivos de fecha.
    async resolveAccountCfg(transaction) {
      const cfg = await this.fetchAccountConfig(transaction);
      if (cfg) {
        return {
          bankType: cfg.check_template || "codevi",
          cal: {
            offsetX: Number(cfg.print_offset_x) || 0,
            offsetY: Number(cfg.print_offset_y) || 0,
            dateOffsetX: Number(cfg.print_date_offset_x) || 0,
            dateOffsetY: Number(cfg.print_date_offset_y) || 0,
            fieldOffsets: cfg.print_field_offsets || {},
            lineFactor: Number(cfg.print_line_factor) || 1.6168,
            pageWidth: Number(cfg.print_page_width) || 0,
            pageHeight: Number(cfg.print_page_height) || 700,
          },
          printerName: cfg.printer_name || this.printerName,
        };
      }
      const bankType = (transaction.bank_id == 20 || transaction.bank_id == 21) ? "popular" : "codevi";
      return { bankType, cal: check_print.getCalibration(bankType), printerName: this.printerName };
    },
    async confirmPrintChecks() {
      if (this.loadingPrint || this.resumePrinting) return;
      if (!this.can.print || this.data_transactions[0].status_id !== 2) {
        this.$toast.error("Se requiere permiso de impresión y una transacción aprobada.");
        return;
      }
      if (!this.printService.online || !this.printService.printerOk) {
        this.$toast.error("El servicio de impresión o la impresora no están disponibles.");
        return;
      }
      const transaction = this.data_transactions[0];
      this.loadingPrint = true;
      try {
        const ChequeDetails = this.buildChequeDetailsFrom(this.selected);
        const { bankType, cal } = await this.resolveAccountCfg(transaction);
        // Valida y maqueta ANTES de reservar: un error aquí no deja registros.
        const pages = check_print.buildCheckPages(bankType, ChequeDetails, cal);
        this.printConfirmDialog = false;
        const jobId = await this.sendPrintJob({ kind: "PRINT", cheques: ChequeDetails, pages, cal });
        if (jobId) {
          // Lote exacto (orden + banco + calibración) para reanudar desde un cheque dañado.
          this.lastBatch = { bankType, cal, printerName: this.printerName, cheques: ChequeDetails };
          this.isMultiple = true;
          this.selected = [];
        }
      } catch (error) {
        console.error(error);
        this.$toast.error(error.message || "Error al imprimir");
      } finally {
        this.loadingPrint = false;
      }
    },
    // Reserva en BD -> envía al API -> guarda el resultado -> pide confirmar el papel.
    // Devuelve el jobId si el lote llegó (o pudo llegar) a la impresora; null si no.
    async sendPrintJob({ kind, reason = null, cheques, pages, cal }) {
      const jobId = this.new_print_job_id();
      const user_id = this.$store.state.auth.user.user.user_id;
      const reserve = await this.api_Post("transactions/checkPrint/reserve", {
        payment_transaction_id: this.data_transactions[0].payment_transaction_id,
        sequences: cheques.map((c) => c.Sequence),
        kind,
        reason,
        printer_name: this.printerName,
        job_id: jobId,
        user_id,
      });
      if (!reserve || reserve.status !== 200) {
        const error = reserve ? await reserve.json().catch(() => null) : null;
        this.$toast.error((error && error.error) || (error && error.message) || "No se pudo registrar la impresión.");
        if (reserve && reserve.status === 409) await this.loadPrintLog();
        return null;
      }

      let status = "SENT";
      let detail = null;
      try {
        const response = await this.api_print_render({
          PrinterName: this.printerName,
          Pages: pages,
          PageWidth: Math.round(Number(cal.pageWidth)) || 0,
          PageHeight: Math.round(Number(cal.pageHeight)) || 0,
          JobId: jobId,
        });
        const warning = check_print.paperWarning(response.result, cal.pageHeight);
        if (warning) this.$toast.warning(warning, { timeout: 20000 });
      } catch (error) {
        detail = error.message;
        status = error.uncertain ? "UNCERTAIN" : "FAILED";
        // Resultado incierto: preguntar al servicio por el lote antes de decidir.
        if (status === "UNCERTAIN") {
          const job = await this.api_print_job(jobId);
          if (job === "submitted") status = "SENT";
          else if (job === "unknown" || job === "rejected") status = "FAILED"; // no llegó a la cola
        }
      }

      const saved = await this.api_Post("transactions/checkPrint/result", { job_id: jobId, status, error: detail, user_id })
        .catch(() => null);
      if (!saved || saved.status !== 200) {
        this.$toast.warning("No se pudo guardar el resultado de la impresión; quedará como «Sin confirmar».");
      }
      await this.loadPrintLog();

      if (status === "FAILED") {
        this.$toast.error(detail || "La impresora rechazó el lote; no se imprimió.");
        return null;
      }
      this.openPostPrint({
        jobId,
        uncertain: status === "UNCERTAIN",
        cheques: cheques.map((c) => ({ seq: c.Sequence, number: c.CheckNumber || c.chequeNumber, name: c.SupplierName })),
      });
      return jobId;
    },
    async loadPrintLog() {
      if (!this.data_transactions || !this.data_transactions[0]) return;
      try {
        const res = await this.api_Post("transactions/checkPrint/history", {
          payment_transaction_id: this.data_transactions[0].payment_transaction_id,
        });
        if (res.status === 200) {
          this.printLog = await res.json();
        } else {
          this.$toast.error("No se pudo cargar el historial de impresión de cheques.");
        }
      } catch (error) {
        console.error(error);
      }
    },
    printStateOf(item) {
      const st = this.printStates[item.payment_transaction_sequence];
      return { ...PRINT_STATES[st ? st.state : "none"], reprints: st ? st.reprints : 0 };
    },
    logStatus(status) {
      return LOG_STATUS[status] || { label: status, color: "grey" };
    },
    openPostPrint(job) {
      this.postPrintJob = job;
      this.postPrintStep = "check";
      this.damagedSeqs = [];
      this.postPrintDialog = true;
    },
    toggleDamaged(seq) {
      this.damagedSeqs = this.damagedSeqs.includes(seq)
        ? this.damagedSeqs.filter((s) => s !== seq)
        : [...this.damagedSeqs, seq];
    },
    async confirmPostPrint() {
      if (this.confirmingPrint) return;
      this.confirmingPrint = true;
      try {
        const res = await this.api_Post("transactions/checkPrint/confirm", {
          job_id: this.postPrintJob.jobId,
          damaged_sequences: this.damagedSeqs,
          user_id: this.$store.state.auth.user.user.user_id,
        });
        const body = await res.json().catch(() => null);
        if (res.status !== 200) {
          this.$toast.error((body && (body.error || body.message)) || "No se pudo confirmar la impresión.");
          await this.loadPrintLog();
          return;
        }
        await this.loadPrintLog();
        if (this.damagedSeqs.length) {
          this.postPrintStep = "damaged";
        } else {
          this.postPrintDialog = false;
          this.$toast.success("Impresión confirmada");
        }
      } finally {
        this.confirmingPrint = false;
      }
    },
    damagedItems() {
      return this.beneficiaries.filter((b) => this.damagedSeqs.includes(b.payment_transaction_sequence));
    },
    // Dañados -> nuevo número de cheque (flujo existente de anular y regenerar).
    voidDamaged() {
      this.postPrintDialog = false;
      this.selected = this.damagedItems();
      this.openVoidDialog();
    },
    // Dañados -> mismo número, desde el primero dañado en adelante, con el motivo prellenado.
    async reprintDamaged() {
      const numbers = this.postPrintJob.cheques.filter((c) => this.damagedSeqs.includes(c.seq)).map((c) => "#" + c.number);
      this.postPrintDialog = false;
      this.selected = this.damagedItems().slice(0, 1);
      await this.openResume();
      this.resumeReason = `Cheque(s) dañado(s) al imprimir: ${numbers.join(", ")}`;
    },
    async openResume() {
      if (this.data_transactions[0].status_id !== 2) {
        this.$toast.warning("Solo se puede reimprimir una transacción aprobada.");
        return;
      }
      const transaction = this.data_transactions[0];
      // Solo se reimprime lo que ya tiene una impresión registrada con su número vigente.
      const printed = (transaction.payment_transaction_details || [])
        .filter((d) => this.printStates[d.payment_transaction_sequence]);
      if (!printed.length) {
        this.$toast.warning("No hay cheques impresos para reimprimir en esta transacción.");
        return;
      }
      // La plantilla y la calibración se leen SIEMPRE de la cuenta: si se ajustaron en
      // "Probar impresora" después de la primera impresión, la reimpresión ya las usa.
      // El orden del lote en memoria se conserva si sigue coincidiendo con lo impreso.
      const printedSeqs = new Set(printed.map((d) => d.payment_transaction_sequence));
      const keepOrder = this.lastBatch && this.lastBatch.cheques.length &&
        this.lastBatch.cheques.every((c) => printedSeqs.has(c.Sequence));
      try {
        const { bankType, cal, printerName } = await this.resolveAccountCfg(transaction);
        const cheques = keepOrder ? this.lastBatch.cheques : this.buildChequeDetailsFrom(printed);
        this.lastBatch = { bankType, cal, printerName, cheques };
      } catch (error) {
        this.$toast.error(error.message || "No se pudo cargar la calibración de la cuenta.");
        return;
      }
      // Preselecciona el cheque marcado (si hay uno) como punto de partida.
      let startIdx = 0;
      if (this.selected.length) {
        const i = this.lastBatch.cheques.findIndex((c) => c.Sequence === this.selected[0].payment_transaction_sequence);
        if (i >= 0) startIdx = i;
      }
      this.printerName = this.lastBatch.printerName || this.printerName;
      this.printService = { online: false, printerOk: false };
      this.resumeIndex = startIdx;
      this.resumeReason = "";
      this.resumeDialog = true;
    },
    async reprintFrom() {
      if (this.loadingPrint || this.resumePrinting) return;
      if (!this.can.print) {
        this.$toast.error("No tienes permiso para imprimir cheques (CXP-014).");
        return;
      }
      if (this.data_transactions[0].status_id !== 2) {
        this.$toast.warning("Solo se puede reimprimir una transacción aprobada.");
        return;
      }
      const reason = this.resumeReason.trim();
      if (reason.length < 5) {
        this.$toast.warning("Indica el motivo de la reimpresión.");
        return;
      }
      if (this.resumeIndex === null || !this.lastBatch) return;
      this.resumePrinting = true;
      try {
        // Plantilla y calibración EN VIVO: se leen de la cuenta al presionar Reimprimir.
        const { bankType, cal } = await this.resolveAccountCfg(this.data_transactions[0]);
        Object.assign(this.lastBatch, { bankType, cal });
        // Reimprime el cheque elegido y TODOS los siguientes, en el mismo orden físico.
        const slice = this.lastBatch.cheques.slice(this.resumeIndex);
        const pages = check_print.buildCheckPages(bankType, slice, cal);
        this.resumeDialog = false;
        const jobId = await this.sendPrintJob({ kind: "REPRINT", reason, cheques: slice, pages, cal });
        if (jobId) this.lastBatch.printerName = this.printerName;
      } catch (error) {
        console.error(error);
        this.$toast.error(error.message || "Error al reimprimir");
      }
      this.resumePrinting = false;
    },
    generarListadoPendiente() {
      const ok = global_reports.generarListadoPendiente(
        this.data_transactions[0],
        check_print.MAX_INVOICES_PER_CHECK
      );
      if (!ok) {
        this.$toast.warning(
          `Ningún cheque supera ${check_print.MAX_INVOICES_PER_CHECK} facturas; no hay listado pendiente.`
        );
      } else {
        this.$toast.success("Listado pendiente generado");
      }
    },
    // Trae la config de impresión de la cuenta emisora del cheque (o null si no aplica).
    async fetchAccountConfig(transaction) {
      const business_unit_id = transaction.business_unit_id;
      const account_id =
        transaction.account_id || transaction.bank_account_id || transaction.account;
      if (!business_unit_id || !account_id) return null;
      const res = await this.api_Post("humanResource/getBusinessAccountPrinterConfig", {
        business_unit_id,
        account_id,
      });
      if (!res || res.status !== 200) {
        throw new Error("No se pudo cargar la calibración de la cuenta. Reintenta antes de imprimir.");
      }
      return await res.json();
    },
    generatePDF() {
      global_reports.generarReporteTransaccion(this.data_transactions[0]);
    },
    async generateExcel() {
      await global_reports.exportarTransaccionExcel(this.data_transactions[0]);
    },
    generateFile() {
      let txt = global_reports.fileDepositPopular(this.data_transactions);
      let depositSequence = this.filLeft("1", 7, "0");
      let transfer_id = this.filLeft(this.data_transactions[0].transfer_id, 5, "0"),
        date = moment(this.data_transactions[0].payment_date).format("MMDD");
      let txtName = `PE${transfer_id}02${date}${depositSequence}E`;
      this.generateTxtFile(txtName, txt);
    },
  },
};
</script>

<style scoped>
.bg-light-soft {
  background-color: #f8fafc;
}

.border-soft {
  border: 1px solid #f1f5f9 !important;
}

.border-bottom {
  border-bottom: 1px solid #f1f5f9 !important;
}

.border-top {
  border-top: 1px solid #f1f5f9 !important;
}

.shadow-sm {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03) !important;
}

.shadow-xl {
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
}

.shadow-xs {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05) !important;
}

.min-h-screen {
  min-height: 100vh;
}

.letter-spacing-1 {
  letter-spacing: 1px;
}

.uppercase {
  text-transform: uppercase;
}

.text-tiny {
  font-size: 0.7rem;
}

.gap-3 {
  gap: 12px;
}

.transition-all {
  transition: all 0.3s ease;
}

.custom-table {
  background-color: white !important;
}

.custom-table>>>th {
  background-color: #f8fafc !important;
  text-transform: uppercase;
  font-size: 0.65rem !important;
  font-weight: 800 !important;
  color: #64748b !important;
  letter-spacing: 0.05em;
  height: 48px !important;
}

.custom-table>>>td {
  height: 64px !important;
}

.cursor-pointer {
  cursor: pointer;
}

.cursor-pointer:hover {
  background-color: rgba(33, 150, 243, 0.02) !important;
}
.min-w-0 {
  min-width: 0;
}

.lh-tight {
  line-height: 1.3 !important;
}

.header-gap,
.action-gap {
  gap: 10px;
}

/* Línea de tiempo */
.steps {
  display: flex;
  flex-wrap: wrap;
  padding: 14px 18px;
  gap: 14px 0;
}

.step {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 220px;
  min-width: 0;
  padding-right: 18px;
}

.step__dot {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e2e8f0;
}

.step--done .step__dot { background: #43a047; }
.step--current .step__dot { background: #fb8c00; box-shadow: 0 0 0 4px #fff3e0; }
.step--error .step__dot { background: #e53935; }

.step__title { color: #475569; letter-spacing: 0.04em; }
.step--todo .step__title { color: #94a3b8; }

.step__line {
  flex: 1;
  height: 2px;
  min-width: 24px;
  background: #e2e8f0;
  margin-left: 8px;
}

.step--done .step__line { background: #a5d6a7; }

/* Tarjeta de siguiente paso */
.action-card {
  border: 1px solid #e2e8f0 !important;
  background: #fff !important;
}

.action-card--warn { border-color: #ffcc80 !important; background: #fffaf3 !important; }
.action-card--info { border-color: #90caf9 !important; background: #f5faff !important; }
.action-card--ok { border-color: #a5d6a7 !important; background: #f6fbf6 !important; }
.action-card--error { border-color: #ef9a9a !important; background: #fff7f7 !important; }

/* Resumen */
.info-tile {
  height: 100%;
  padding: 12px 16px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #eef2f7;
}

.info-tile__label {
  font-size: 0.7rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #64748b;
  margin-bottom: 4px;
}

.info-tile__value {
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1.2;
  color: #1e293b;
}

.info-tile__value--sm {
  font-size: 1rem;
}

.info-tile__hint {
  font-size: 0.72rem;
  color: #94a3b8;
}

.info-tile__row {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.7;
}

.info-tile__row strong {
  color: #334155;
}

.invoice-panels .v-expansion-panel::before {
  box-shadow: none !important;
}
</style>
